/**
 * Shared display-name formatters for plan tiers and role IDs.
 * Extracted from TeamSwitcher.tsx and UserMenu.tsx.
 */

/** Map a plan_id to its human-readable tier label. */
export const formatPlanName = (planId: string | undefined): string => {
  switch (planId) {
    case 'institutional': return 'Institutional';
    case 'advisor': return 'Advisor';
    case 'basic': return 'Basic';
    default: return 'Free';
  }
};

/**
 * Format a decimal weight (0–1) as a percentage string, e.g. 0.0512 → "5.12%".
 *
 * With `smart: true`, a non-zero weight that would round to all zeros at the base
 * precision escalates precision (up to 4 decimals, matching the CSV export) so tiny
 * holdings aren't misrepresented as "0.00%"; anything still below 0.0001% renders as
 * "<0.0001%" rather than a misleading zero.
 */
export const formatWeight = (
  w: number | null | undefined,
  opts?: { precision?: number; nullDisplay?: string; smart?: boolean }
): string => {
  if (w === null || w === undefined) return opts?.nullDisplay ?? '0%';
  const pct = w * 100;
  const base = opts?.precision ?? 2;
  if (opts?.smart && pct !== 0) {
    let precision = base;
    while (precision < 4 && Number(pct.toFixed(precision)) === 0) precision++;
    if (Number(pct.toFixed(precision)) === 0) return pct > 0 ? '<0.0001%' : '>-0.0001%';
    return `${pct.toFixed(precision)}%`;
  }
  return `${pct.toFixed(base)}%`;
};

/** Format a numeric AUM value into a compact display string, e.g. 1234567890 → "$1.2B". */
export const formatAum = (value: number | null | undefined): string => {
  if (value == null) return '--';
  const abs = Math.abs(value);
  if (abs >= 1e9)  return `$${(value / 1e9).toFixed(1)}B`;
  if (abs >= 1e6)  return `$${(value / 1e6).toFixed(0)}M`;
  if (abs >= 1e3)  return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

/** Map a role_id to its human-readable role label. */
export const formatRoleName = (roleId: string | null | undefined): string => {
  switch (roleId) {
    case 'admin': return 'Admin';
    case 'collaborator': return 'Collaborator';
    case 'view_only': return 'Viewer';
    default: return roleId || '';
  }
};
